import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import ComicCard from '@/components/base/ComicCard';
import { getComicById, comics } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  const comic = getComicById(id || '');

  if (!comic) {
    return (
      <>
        <SEOHead
          title="漫画未找到 - 韩国漫画网"
          description="抱歉，您查找的漫画不存在或已下架。"
        />
        <Navbar />
        <main className="pt-24 pb-10">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto text-center py-20">
              <div className="w-20 h-20 mx-auto flex items-center justify-center bg-background-100 rounded-full mb-4">
                <i className="ri-file-unknow-line text-foreground-400 text-3xl"></i>
              </div>
              <h1 className="text-xl font-bold text-foreground-900 mb-2">漫画未找到</h1>
              <p className="text-foreground-500 mb-6">抱歉，您查找的漫画不存在或已下架</p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 bg-primary-500 text-background-50 px-5 py-2.5 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-home-line"></i>
                返回首页
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedComics = comics
    .filter((c) => c.categorySlug === comic.categorySlug && c.id !== comic.id)
    .slice(0, 4);

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 韩国漫画在线观看`,
    description: comic.description,
    author: { '@type': 'Person', name: comic.author },
    datePublished: '2026-01-01',
    dateModified: comic.updateDate,
    url: `https://www.koreancomic.cn/comic/${comic.id}`,
    image: comic.coverUrl,
  };

  return (
    <>
      <SEOHead
        title={`${comic.title} - 韩国漫画网 | 免费韩漫在线看`}
        description={`${comic.title} - ${comic.description.substring(0, 120)}。韩国漫画网免费提供${comic.title}高清在线观看，每日更新最新章节。`}
        keywords={`${comic.title},韩漫,${comic.category}漫画,免费韩漫,韩国漫画,在线漫画`}
        canonicalUrl={`https://www.koreancomic.cn/comic/${comic.id}`}
        ogImage={comic.coverUrl}
        ogType="article"
        structuredData={structuredData}
      />

      <Navbar />

      <main className="pt-24 pb-10 md:pb-14">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: `${comic.category}漫画`, href: `/category/${comic.categorySlug}` },
                { label: comic.title },
              ]}
            />

            <div className="flex flex-col lg:flex-row gap-8 md:gap-10">
              <div className="lg:w-[300px] flex-shrink-0">
                <div className="sticky top-24">
                  <div className="aspect-[2/3] rounded-lg overflow-hidden bg-background-100">
                    <img
                      src={comic.coverUrl}
                      alt={`${comic.title} 封面`}
                      title={`${comic.title} - 韩国漫画`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <div className="mb-6">
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                      comic.status === '连载中'
                        ? 'bg-primary-100 text-primary-700'
                        : 'bg-secondary-100 text-secondary-700'
                    }`}>
                      {comic.status}
                    </span>
                    <span className="text-xs bg-accent-100 text-accent-700 px-2.5 py-1 rounded-full">
                      {comic.category}
                    </span>
                  </div>
                  <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                    {comic.title}
                  </h1>
                  <div className="flex flex-wrap items-center gap-3 text-sm text-foreground-500 mb-4">
                    <span>作者：<strong className="text-foreground-800">{comic.author}</strong></span>
                    <span className="text-foreground-300">|</span>
                    <span className="flex items-center gap-1">
                      <i className="ri-star-fill text-accent-500"></i>
                      <strong className="text-foreground-800">{comic.rating}</strong> 分
                    </span>
                    <span className="text-foreground-300">|</span>
                    <span>
                      <i className="ri-eye-line mr-1"></i>
                      {(comic.views / 10000).toFixed(0)}万次阅读
                    </span>
                    <span className="text-foreground-300">|</span>
                    <span>共 <strong className="text-foreground-800">{comic.chapters.length}</strong> 话</span>
                  </div>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {comic.tags.map((tag) => (
                      <span
                        key={tag}
                        className="text-xs bg-background-100 border border-background-200/70 text-foreground-600 px-2.5 py-1 rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  <article className="prose-sm text-foreground-700 leading-relaxed bg-background-100 rounded-lg p-4 md:p-5">
                    <h2 className="font-heading text-base font-semibold text-foreground-900 mb-2">作品简介</h2>
                    <p>{comic.description}</p>
                  </article>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-heading text-lg font-bold text-foreground-900">章节列表</h2>
                    <div className="flex items-center gap-2">
                      <button className="text-xs text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap">
                        正序
                      </button>
                      <span className="text-foreground-300 text-xs">|</span>
                      <button className="text-xs text-foreground-500 hover:text-foreground-800 font-medium whitespace-nowrap">
                        倒序
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1">
                    {comic.chapters.map((chapter) => (
                      <Link
                        key={chapter.id}
                        to={`/comic/${comic.id}`}
                        className="flex items-center justify-between p-3.5 bg-background-100 rounded-lg hover:bg-primary-50 transition-colors group"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="text-xs text-foreground-400 font-mono w-12 flex-shrink-0">
                            第{chapter.number}话
                          </span>
                          <span className="text-sm font-medium text-foreground-800 line-clamp-1 group-hover:text-primary-600 transition-colors">
                            {chapter.title}
                          </span>
                        </div>
                        <span className="text-xs text-foreground-400 flex-shrink-0 ml-4">
                          {chapter.updateDate}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {relatedComics.length > 0 && (
              <section className="mt-12 md:mt-16 pt-10 border-t border-background-200/70">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900">
                      相关推荐
                    </h2>
                    <p className="text-sm text-foreground-500 mt-1">更多{comic.category}题材漫画</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop="">
                  {relatedComics.map((c) => (
                    <ComicCard key={c.id} comic={c} />
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
}

